import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getConsultationPath } from "../../utils/consultationLink";
import { WEB_LEAD_MODULES } from "../../constants/webLeadModules";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import "./TermsAndConditions.css";

const SECTIONS = [
  {
    id: "eligibility",
    num: "01",
    icon: "fa-user-check",
    title: "Eligibility",
    color: "#2563eb",
    content:
      "You must be at least 18 years old and legally capable of entering into contracts under Indian law to access or use the Wealth Compounders platform.",
  },
  {
    id: "nature",
    num: "02",
    icon: "fa-briefcase",
    title: "Nature of Services",
    color: "#0891b2",
    content:
      "Wealth Compounders provides financial advisory, investment consulting, portfolio analysis, and educational services. All investments are subject to market risks. Returns are not guaranteed. Past performance does not indicate future results.",
  },
  {
    id: "guarantee",
    num: "03",
    icon: "fa-chart-line",
    title: "No Investment Guarantee",
    color: "#7c3aed",
    content:
      "Information on the Platform is for advisory and informational purposes only. We are not SEBI registered and do not constitute a guarantee of returns. Users are solely responsible for their investment decisions.",
  },
  {
    id: "account",
    num: "04",
    icon: "fa-circle-user",
    title: "User Account",
    color: "#10b981",
    content:
      "Users must provide accurate information and maintain confidentiality of login credentials. You are responsible for all activity that occurs under your account.",
  },
  {
    id: "kyc",
    num: "05",
    icon: "fa-id-card",
    title: "KYC & Compliance",
    color: "#f59e0b",
    content:
      "Users may be required to complete KYC and regulatory verification as per applicable laws. Failure to comply may result in suspension or termination of services.",
  },
  {
    id: "ip",
    num: "06",
    icon: "fa-copyright",
    title: "Intellectual Property",
    color: "#ef4444",
    content:
      "All content, branding, tools, reports, and software on the Platform are the exclusive property of Wealth Compounders and may not be copied, reproduced, or distributed without prior written permission.",
  },
  {
    id: "liability",
    num: "07",
    icon: "fa-shield-halved",
    title: "Limitation of Liability",
    color: "#8b5cf6",
    content:
      "The Company is not liable for market losses, trading losses, indirect damages, technical failures, or third-party service disruptions. Use of the Platform is entirely at your own risk.",
  },
  {
    id: "third-party",
    num: "08",
    icon: "fa-link",
    title: "Third-Party Services",
    color: "#0891b2",
    content:
      "The Platform is integrated with third-party data providers and services. The Company is not responsible for their actions, accuracy, availability, or performance.",
  },
  {
    id: "suspension",
    num: "09",
    icon: "fa-ban",
    title: "Suspension & Termination",
    color: "#dc2626",
    content:
      "Access to the Platform may be suspended or permanently terminated for violation of these Terms, fraudulent activity, regulatory requirements, or any other conduct deemed harmful.",
  },
  {
    id: "governing",
    num: "10",
    icon: "fa-scale-balanced",
    title: "Governing Law",
    color: "#2563eb",
    content:
      "These Terms and Conditions are governed by and construed in accordance with the laws of India & SEBI",
  },
];

export default function TermsAndConditions() {
  const navigate = useNavigate();
  const [active, setActive] = useState("eligibility");
  const rightPanelRef = useRef(null);

  /* Scroll-spy on the RIGHT panel scroll events */
  useEffect(() => {
    const panel = rightPanelRef.current;
    if (!panel) return;
    const handler = () => {
      const scrollTop = panel.scrollTop + 60;
      for (let i = SECTIONS.length - 1; i >= 0; i--) {
        const el = document.getElementById("tc-" + SECTIONS[i].id);
        if (el && el.offsetTop <= scrollTop) {
          setActive(SECTIONS[i].id);
          break;
        }
      }
    };
    panel.addEventListener("scroll", handler, { passive: true });
    return () => panel.removeEventListener("scroll", handler);
  }, []);

  /* Scroll the RIGHT panel on TOC click */
  const scrollTo = (id) => {
    const panel = rightPanelRef.current;
    const el = document.getElementById("tc-" + id);
    if (panel && el) {
      panel.scrollTo({ top: el.offsetTop - 32, behavior: "smooth" });
      setActive(id);
    }
  };

  return (
    <div className="wtc-wrapper">
      <Navbar />

      {/* HERO */}
      <section className="wtc-hero">
        <div className="wtc-hero-bg" />
        <div className="wtc-hero-dotgrid" />
        <div className="wtc-hero-glow wtc-glow-l" />
        <div className="wtc-hero-glow wtc-glow-r" />
        <div className="wtc-hero-inner">
          <div className="wtc-hero-eyebrow">
            <i className="fas fa-file-contract" /> Legal
          </div>
          <h1 className="wtc-hero-title">Terms &amp; Conditions</h1>
          <p className="wtc-hero-sub">
            By accessing or using the Wealth Compounders platform, you agree to
            be bound by the terms set out below. Please read them carefully
            before proceeding.
          </p>
          <div className="wtc-hero-meta">
            <span className="wtc-meta-chip">
              <i className="fas fa-calendar-check" /> Effective: January 2025
            </span>
            <span className="wtc-meta-chip">
              <i className="fas fa-gavel" /> Governed by Indian Law
            </span>
            <span className="wtc-meta-chip">
              <i className="fas fa-list-ol" /> 10 Sections
            </span>
          </div>
        </div>
      </section>

      {/* SPLIT PANEL */}
      <div className="wtc-split">
        <div className="wtc-split-bg" />
        <div className="wtc-split-inner">
          {/* LEFT: fixed TOC */}
          <aside className="wtc-toc">
            <div className="wtc-toc-card">
              <p className="wtc-toc-heading">
                <i className="fas fa-list" /> Table of Contents
              </p>
              <nav className="wtc-toc-nav">
                {SECTIONS.map((s) => (
                  <button
                    key={s.id}
                    className={
                      "wtc-toc-item" +
                      (active === s.id ? " wtc-toc-active" : "")
                    }
                    onClick={() => scrollTo(s.id)}
                    style={
                      active === s.id
                        ? { color: s.color, borderLeftColor: s.color }
                        : {}
                    }
                  >
                    <span className="wtc-toc-num">{s.num}</span>
                    <span className="wtc-toc-lbl">{s.title}</span>
                  </button>
                ))}
              </nav>
              <div className="wtc-toc-cta">
                <p>Questions about our terms?</p>
                <button
                  className="wtc-toc-btn"
                  onClick={() => navigate(getConsultationPath(WEB_LEAD_MODULES.TERMS))}
                >
                  Contact Us <i className="fas fa-arrow-right" />
                </button>
              </div>
            </div>
          </aside>

          {/* RIGHT: independently scrollable */}
          <main className="wtc-content" ref={rightPanelRef}>
            <div className="wtc-content-inner">
              <div className="wtc-notice">
                <i className="fas fa-circle-info" />
                <p>
                  These Terms constitute a legally binding agreement between you
                  and <strong>Wealth Compounders</strong>. Continued use of the
                  Platform constitutes acceptance of any updates to these Terms.
                </p>
              </div>

              {SECTIONS.map((s, i) => (
                <div
                  key={s.id}
                  id={"tc-" + s.id}
                  className="wtc-section"
                  style={{ animationDelay: i * 50 + "ms" }}
                >
                  <div className="wtc-sec-head">
                    <div
                      className="wtc-sec-icon"
                      style={{
                        background: s.color + "12",
                        border: "1.5px solid " + s.color + "22",
                        color: s.color,
                      }}
                    >
                      <i className={"fas " + s.icon} />
                    </div>
                    <div className="wtc-sec-title-group">
                      <span className="wtc-sec-num" style={{ color: s.color }}>
                        {s.num}
                      </span>
                      <h2 className="wtc-sec-title">{s.title}</h2>
                    </div>
                    <div
                      className="wtc-sec-badge"
                      style={{
                        background: s.color + "10",
                        color: s.color,
                        border: "1px solid " + s.color + "25",
                      }}
                    >
                      Section {s.num}
                    </div>
                  </div>
                  <div
                    className="wtc-sec-body"
                    style={{ borderLeftColor: s.color + "45" }}
                  >
                    <p className="wtc-sec-text">{s.content}</p>
                  </div>
                  {i < SECTIONS.length - 1 && (
                    <div className="wtc-sec-divider" />
                  )}
                </div>
              ))}

              <div className="wtc-acceptance">
                <i className="fas fa-check-circle" />
                <div>
                  <h3>Your Acceptance</h3>
                  <p>
                    By accessing or using any part of the Wealth Compounders
                    platform, you acknowledge that you have read, understood,
                    and agree to be bound by these Terms and Conditions.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer inside scrollable right panel */}
          </main>
        </div>
      </div>
      <Footer />
    </div>
  );
}
